import { useEffect, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  Plus, Search, Edit, Trash2, Users, X, Calendar, Clock, DollarSign,
  Mail, Phone, Award, TrendingUp, User, Eye, CheckCircle, AlertCircle,
  Briefcase, Star, Activity, BarChart3
} from 'lucide-react'
import { staffAPI, bookingsAPI } from '../lib/api'
import { Button } from '../components/ui/Button'
import { Input } from '../components/ui/Input'
import { Textarea } from '../components/ui/Textarea'
import { useToast } from '../components/ui/useToast'

type Staff = {
  id: number
  name: string
  email: string
  phone?: string
  role?: string
  created_at: string
}

export function StaffPageNew() {
  const [searchTerm, setSearchTerm] = useState('')
  const [showModal, setShowModal] = useState(false)
  const [editingStaff, setEditingStaff] = useState<Staff | null>(null)
  const [deletingStaff, setDeletingStaff] = useState<Staff | null>(null)
  const [viewingStaff, setViewingStaff] = useState<Staff | null>(null)
  const { notify } = useToast()

  const {
    data: staffResponse,
    isLoading,
    error,
    refetch
  } = useQuery({
    queryKey: ['staff'],
    queryFn: () => staffAPI.list()
  })

  const staff = Array.isArray(staffResponse?.data) ? staffResponse.data : []
  const filteredStaff = staff.filter((s: Staff) =>
    searchTerm === '' ||
    s.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    s.email.toLowerCase().includes(searchTerm.toLowerCase())
  )

  const handleDelete = async () => {
    if (!deletingStaff) return
    try {
      const res = await staffAPI.delete(deletingStaff.id)
      if (res.success) {
        notify({ kind: 'success', message: 'Staff member deleted successfully' })
        setDeletingStaff(null)
        refetch()
      } else {
        notify({ kind: 'error', message: res.message || 'Failed to delete staff member' })
      }
    } catch (err) {
      notify({ kind: 'error', message: 'Failed to delete staff member' })
    }
  }

  // Calculate stats
  const stats = {
    total: staff.length,
    active: staff.filter((s: any) => s.total_bookings > 0).length,
    avgBookings: staff.length > 0
      ? Math.round(staff.reduce((sum: number, s: any) => sum + (s.total_bookings || 0), 0) / staff.length)
      : 0
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <div className="text-red-600 dark:text-red-400 mb-4">
          Failed to load staff members. Please try again.
        </div>
        <Button onClick={() => refetch()}>Retry</Button>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold bg-gradient-to-r from-orange-600 to-red-600 bg-clip-text text-transparent">
            Staff Management
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Manage team members, schedules, and performance
          </p>
        </div>
        <Button
          onClick={() => setShowModal(true)}
          className="bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 flex items-center gap-2"
        >
          <Plus className="h-5 w-5" />
          Add Staff
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid sm:grid-cols-3 gap-4">
        <div className="card bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-500/5 dark:to-indigo-500/5 border-blue-200 dark:border-blue-800">
          <div className="card-body">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-blue-600 dark:text-blue-400">Total Staff</p>
                <p className="text-3xl font-bold mt-2 text-blue-900 dark:text-blue-100">{stats.total}</p>
              </div>
              <div className="p-3 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-500">
                <Users className="h-5 w-5 text-white" />
              </div>
            </div>
          </div>
        </div>

        <div className="card bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-500/5 dark:to-emerald-500/5 border-green-200 dark:border-green-800">
          <div className="card-body">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-green-600 dark:text-green-400">Active Staff</p>
                <p className="text-3xl font-bold mt-2 text-green-900 dark:text-green-100">{stats.active}</p>
              </div>
              <div className="p-3 rounded-xl bg-gradient-to-br from-green-500 to-emerald-500">
                <CheckCircle className="h-5 w-5 text-white" />
              </div>
            </div>
          </div>
        </div>

        <div className="card bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-500/5 dark:to-pink-500/5 border-purple-200 dark:border-purple-800">
          <div className="card-body">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-purple-600 dark:text-purple-400">Avg Bookings</p>
                <p className="text-3xl font-bold mt-2 text-purple-900 dark:text-purple-100">{stats.avgBookings}</p>
              </div>
              <div className="p-3 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500">
                <BarChart3 className="h-5 w-5 text-white" />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="card">
        <div className="card-body">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <Input
              type="text"
              placeholder="Search staff by name or email..."
              className="pl-10"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* Staff Grid */}
      <div className="card">
        <div className="card-header flex items-center gap-2">
          <Users className="h-5 w-5 text-orange-600" />
          <h2 className="font-semibold text-lg">Team Members ({filteredStaff.length})</h2>
        </div>
        <div className="card-body">
          {isLoading ? (
            <div className="space-y-3">
              {[1, 2, 3].map(i => (
                <div key={i} className="p-4 bg-gray-50 dark:bg-gray-800/50 rounded-xl animate-pulse">
                  <div className="h-5 bg-gray-200 dark:bg-gray-700 rounded w-1/3 mb-3"></div>
                  <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-full mb-2"></div>
                </div>
              ))}
            </div>
          ) : filteredStaff.length === 0 ? (
            <div className="text-center py-12">
              <Users className="h-16 w-16 text-gray-300 dark:text-gray-700 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">No staff members found</h3>
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                {searchTerm ? 'No staff match your search.' : 'Get started by adding your first team member.'}
              </p>
              <Button onClick={() => setShowModal(true)} className="bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600">
                <Plus className="h-4 w-4 mr-2" />
                Add Staff
              </Button>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredStaff.map((member: any) => (
                <div
                  key={member.id}
                  className="p-4 rounded-xl border-2 border-gray-200 dark:border-gray-800 hover:border-orange-500 dark:hover:border-orange-500 transition-all"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-orange-500 to-red-500 flex items-center justify-center text-white font-bold text-lg">
                        {member.name.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <h3 className="font-semibold text-lg">{member.name}</h3>
                        {member.role && (
                          <div className="flex items-center gap-1 text-xs">
                            <Briefcase className="h-3 w-3 text-orange-500" />
                            <span className="text-gray-500">{member.role}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2 text-sm mb-4">
                    <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                      <Mail className="h-3 w-3" />
                      <span className="truncate">{member.email}</span>
                    </div>
                    {member.phone && (
                      <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                        <Phone className="h-3 w-3" />
                        {member.phone}
                      </div>
                    )}
                    {member.total_bookings !== undefined && (
                      <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                        <Calendar className="h-3 w-3" />
                        {member.total_bookings} bookings
                      </div>
                    )}
                  </div>

                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setViewingStaff(member)}
                      className="flex-1 text-xs"
                    >
                      <Eye className="h-3 w-3 mr-1" />
                      View
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setEditingStaff(member)}
                      className="text-xs"
                    >
                      <Edit className="h-3 w-3" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setDeletingStaff(member)}
                      className="text-red-600 border-red-600 hover:bg-red-50 dark:hover:bg-red-500/10 text-xs"
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      {showModal && (
        <AddStaffModal
          onClose={() => setShowModal(false)}
          onCreated={() => {
            notify({ kind: 'success', message: 'Staff member added successfully' })
            setShowModal(false)
            refetch()
          }}
        />
      )}

      {editingStaff && (
        <EditStaffModal
          staff={editingStaff}
          onClose={() => setEditingStaff(null)}
          onUpdated={() => {
            notify({ kind: 'success', message: 'Staff member updated successfully' })
            setEditingStaff(null)
            refetch()
          }}
        />
      )}

      {viewingStaff && (
        <StaffDetailModal
          staff={viewingStaff}
          onClose={() => setViewingStaff(null)}
          onEdit={() => {
            setEditingStaff(viewingStaff)
            setViewingStaff(null)
          }}
        />
      )}

      {deletingStaff && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-xl max-w-md w-full">
            <div className="px-6 py-5 border-b border-gray-200 dark:border-gray-800">
              <h2 className="text-xl font-bold">Delete Staff Member</h2>
            </div>
            <div className="p-6">
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                Are you sure you want to delete <span className="font-semibold">{deletingStaff.name}</span>? This action cannot be undone.
              </p>
              <div className="flex items-center justify-end gap-3">
                <Button variant="outline" onClick={() => setDeletingStaff(null)}>
                  Cancel
                </Button>
                <Button
                  onClick={handleDelete}
                  className="bg-red-600 hover:bg-red-700 text-white"
                >
                  Delete
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

function AddStaffModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [form, setForm] = useState({ name: '', email: '', phone: '', role: '' })
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  const setField = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    setForm((prev) => ({ ...prev, [name]: value }))
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: '' }))
  }

  const validate = () => {
    const e: Record<string, string> = {}
    if (!form.name.trim()) e.name = 'Name is required'
    if (!form.email.trim()) e.email = 'Email is required'
    else if (!/^[^@]+@[^@]+\.[^@]+$/.test(form.email)) e.email = 'Email is invalid'
    setErrors(e)
    return Object.keys(e).length === 0
  }

  const handleSubmit = async (ev: React.FormEvent) => {
    ev.preventDefault()
    if (!validate()) return
    setSubmitting(true)
    try {
      await staffAPI.create({
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone || undefined,
        role: form.role || undefined
      })
      onCreated()
    } catch (err: any) {
      const backendErrors = err?.response?.data?.errors
      if (backendErrors && typeof backendErrors === 'object') {
        const mapped: Record<string, string> = {}
        for (const key of Object.keys(backendErrors)) {
          const val = backendErrors[key]
          mapped[key] = Array.isArray(val) ? String(val[0]) : String(val)
        }
        setErrors(mapped)
      }
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-white dark:bg-gray-900 rounded-2xl shadow-xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-gradient-to-br from-orange-50 to-red-50 dark:from-orange-500/10 dark:to-red-500/10 px-6 py-5 border-b border-gray-200 dark:border-gray-800 rounded-t-2xl">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-orange-500 to-red-500 flex items-center justify-center">
                <Plus className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="text-xl font-bold">Add Staff Member</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">Create a new team member</p>
              </div>
            </div>
            <button className="p-2 hover:bg-gray-200 dark:hover:bg-gray-800 rounded-xl transition-colors" onClick={onClose}>
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="form-label flex items-center gap-2">
              <User className="h-4 w-4 text-gray-500" />
              Full name *
            </label>
            <Input name="name" value={form.name} onChange={setField} placeholder="e.g., John Doe" required />
            {errors.name && <p className="text-sm text-red-600 mt-1">{errors.name}</p>}
          </div>
          <div>
            <label className="form-label flex items-center gap-2">
              <Mail className="h-4 w-4 text-gray-500" />
              Email *
            </label>
            <Input name="email" type="email" value={form.email} onChange={setField} placeholder="name@example.com" required />
            {errors.email && <p className="text-sm text-red-600 mt-1">{errors.email}</p>}
          </div>
          <div>
            <label className="form-label flex items-center gap-2">
              <Phone className="h-4 w-4 text-gray-500" />
              Phone
            </label>
            <Input name="phone" value={form.phone} onChange={setField} placeholder="Optional" />
          </div>
          <div>
            <label className="form-label flex items-center gap-2">
              <Briefcase className="h-4 w-4 text-gray-500" />
              Role
            </label>
            <Input name="role" value={form.role} onChange={setField} placeholder="e.g., Manager, Instructor" />
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-800">
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button
              type="submit"
              disabled={submitting}
              className="bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600"
            >
              {submitting ? 'Creating...' : 'Create Staff Member'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}

function EditStaffModal({ staff, onClose, onUpdated }: { staff: Staff; onClose: () => void; onUpdated: () => void }) {
  const [form, setForm] = useState({
    name: staff.name,
    email: staff.email,
    phone: staff.phone || '',
    role: (staff as any).role || ''
  })
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  const setField = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    setForm((prev) => ({ ...prev, [name]: value }))
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: '' }))
  }

  const validate = () => {
    const e: Record<string, string> = {}
    if (!form.name.trim()) e.name = 'Name is required'
    if (!form.email.trim()) e.email = 'Email is required'
    else if (!/^[^@]+@[^@]+\.[^@]+$/.test(form.email)) e.email = 'Email is invalid'
    setErrors(e)
    return Object.keys(e).length === 0
  }

  const handleSubmit = async (ev: React.FormEvent) => {
    ev.preventDefault()
    if (!validate()) return
    setSubmitting(true)
    try {
      await staffAPI.update(staff.id, {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone || undefined,
        role: form.role || undefined
      })
      onUpdated()
    } catch (err: any) {
      const backendErrors = err?.response?.data?.errors
      if (backendErrors && typeof backendErrors === 'object') {
        const mapped: Record<string, string> = {}
        for (const key of Object.keys(backendErrors)) {
          const val = backendErrors[key]
          mapped[key] = Array.isArray(val) ? String(val[0]) : String(val)
        }
        setErrors(mapped)
      }
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-white dark:bg-gray-900 rounded-2xl shadow-xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-gradient-to-br from-orange-50 to-red-50 dark:from-orange-500/10 dark:to-red-500/10 px-6 py-5 border-b border-gray-200 dark:border-gray-800 rounded-t-2xl">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-orange-500 to-red-500 flex items-center justify-center">
                <Edit className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="text-xl font-bold">Edit Staff Member</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">Update team member information</p>
              </div>
            </div>
            <button className="p-2 hover:bg-gray-200 dark:hover:bg-gray-800 rounded-xl transition-colors" onClick={onClose}>
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="form-label flex items-center gap-2">
              <User className="h-4 w-4 text-gray-500" />
              Full name *
            </label>
            <Input name="name" value={form.name} onChange={setField} required />
            {errors.name && <p className="text-sm text-red-600 mt-1">{errors.name}</p>}
          </div>
          <div>
            <label className="form-label flex items-center gap-2">
              <Mail className="h-4 w-4 text-gray-500" />
              Email *
            </label>
            <Input name="email" type="email" value={form.email} onChange={setField} required />
            {errors.email && <p className="text-sm text-red-600 mt-1">{errors.email}</p>}
          </div>
          <div>
            <label className="form-label flex items-center gap-2">
              <Phone className="h-4 w-4 text-gray-500" />
              Phone
            </label>
            <Input name="phone" value={form.phone} onChange={setField} />
          </div>
          <div>
            <label className="form-label flex items-center gap-2">
              <Briefcase className="h-4 w-4 text-gray-500" />
              Role
            </label>
            <Input name="role" value={form.role} onChange={setField} />
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-800">
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button
              type="submit"
              disabled={submitting}
              className="bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600"
            >
              {submitting ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}

function StaffDetailModal({ staff, onClose, onEdit }: { staff: any; onClose: () => void; onEdit: () => void }) {
  const [bookings, setBookings] = useState<any[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const loadBookings = async () => {
      try {
        const res = await bookingsAPI.list({ staff_id: staff.id })
        if (res.success) {
          setBookings(Array.isArray(res.data) ? res.data : [])
        }
      } catch (e) {
        console.error('Failed to load bookings', e)
      } finally {
        setLoading(false)
      }
    }
    loadBookings()
  }, [staff.id])

  const totalRevenue = bookings.reduce((sum, b) => sum + (b.total_amount || 0), 0)

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="w-full max-w-3xl bg-white dark:bg-gray-900 rounded-2xl shadow-xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-gradient-to-br from-orange-50 to-red-50 dark:from-orange-500/10 dark:to-red-500/10 px-6 py-5 border-b border-gray-200 dark:border-gray-800 rounded-t-2xl">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-orange-500 to-red-500 flex items-center justify-center text-white font-bold text-2xl">
                {staff.name.charAt(0).toUpperCase()}
              </div>
              <div>
                <h3 className="text-2xl font-bold">{staff.name}</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">{staff.email}</p>
                {staff.role && (
                  <p className="text-xs text-orange-600 dark:text-orange-400 mt-1">{staff.role}</p>
                )}
              </div>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={onEdit}>
                <Edit className="h-4 w-4 mr-1" />
                Edit
              </Button>
              <button className="p-2 hover:bg-gray-200 dark:hover:bg-gray-800 rounded-xl transition-colors" onClick={onClose}>
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {/* Quick Stats */}
          <div className="grid grid-cols-2 gap-4">
            <div className="card bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-500/10 dark:to-indigo-500/10">
              <div className="card-body text-center">
                <Calendar className="h-6 w-6 text-blue-600 dark:text-blue-400 mx-auto mb-2" />
                <p className="text-2xl font-bold text-blue-900 dark:text-blue-100">{bookings.length}</p>
                <p className="text-xs text-blue-600 dark:text-blue-400">Total Bookings</p>
              </div>
            </div>
            <div className="card bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-500/10 dark:to-emerald-500/10">
              <div className="card-body text-center">
                <DollarSign className="h-6 w-6 text-green-600 dark:text-green-400 mx-auto mb-2" />
                <p className="text-2xl font-bold text-green-900 dark:text-green-100">£{(totalRevenue / 100).toFixed(2)}</p>
                <p className="text-xs text-green-600 dark:text-green-400">Revenue Generated</p>
              </div>
            </div>
          </div>

          {/* Contact Info */}
          <div className="card">
            <div className="card-header">
              <h4 className="font-semibold">Contact Information</h4>
            </div>
            <div className="card-body space-y-2">
              <div className="flex items-center gap-2 text-sm">
                <Mail className="h-4 w-4 text-gray-500" />
                <span className="text-gray-600 dark:text-gray-400">{staff.email}</span>
              </div>
              {staff.phone && (
                <div className="flex items-center gap-2 text-sm">
                  <Phone className="h-4 w-4 text-gray-500" />
                  <span className="text-gray-600 dark:text-gray-400">{staff.phone}</span>
                </div>
              )}
              <div className="flex items-center gap-2 text-sm">
                <Calendar className="h-4 w-4 text-gray-500" />
                <span className="text-gray-600 dark:text-gray-400">
                  Joined {new Date(staff.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
                </span>
              </div>
            </div>
          </div>

          {/* Recent Bookings */}
          <div className="card">
            <div className="card-header flex items-center gap-2">
              <Activity className="h-5 w-5 text-orange-600" />
              <h4 className="font-semibold">Recent Bookings</h4>
            </div>
            <div className="card-body">
              {loading ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600 mx-auto"></div>
                </div>
              ) : bookings.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  No bookings assigned yet
                </div>
              ) : (
                <div className="space-y-2">
                  {bookings.slice(0, 10).map(b => (
                    <div key={b.id} className="p-3 rounded-lg border border-gray-200 dark:border-gray-800 flex items-center justify-between">
                      <div className="flex-1">
                        <p className="text-sm font-medium">
                          {new Date(b.start_time).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                        </p>
                        <p className="text-xs text-gray-500">
                          {new Date(b.start_time).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })} - {new Date(b.end_time).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-medium">£{((b.total_amount || 0) / 100).toFixed(2)}</p>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${
                          b.status === 'confirmed' ? 'bg-green-100 text-green-700 dark:bg-green-500/10 dark:text-green-300' :
                          b.status === 'pending' ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-500/10 dark:text-yellow-300' :
                          'bg-red-100 text-red-700 dark:bg-red-500/10 dark:text-red-300'
                        }`}>
                          {b.status}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
